import { Info } from "lucide-react";
import type { ReactNode } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/components/ui/tooltip";
import { cn } from "@/shared/lib/cn";

interface Props {
  /// The word being explained. Bolded in the bubble and used as the
  /// accessible name ("Explain Benchmark"), so it should read as a term,
  /// not a sentence.
  term: string;
  /// One or two plain sentences. Avoid defining a term with other jargon.
  definition: string;
  /// Optional concrete illustration, rendered smaller beneath.
  example?: string;
  className?: string;
}

/// The single "what does this word mean" affordance: a small Info button
/// that opens a short definition.
///
/// Three near-identical implementations of this existed before it
/// (`FieldTooltip` for forms, `GoalSectionInfo` for goal cards, and a
/// hand-rolled `CircleHelp` for the comparison heatmap's Benchmark row),
/// which is one past A7's "three before extract". `FieldTooltip` now
/// delegates here, so form fields keep their own prop names and every
/// bubble on the app looks the same.
///
/// No `TooltipProvider`: one is mounted app-wide in `app/providers.tsx`
/// with `delayDuration={150}`. Nesting another resets the delay to 0 for
/// its subtree, which is exactly why the heatmap's benchmark tooltip used
/// to pop instantly while every other tooltip on the page waited.
///
/// The icon is 14px for visual weight but the button carries an invisible
/// `before:-inset-3` expansion, so the real hit target clears the 44px
/// touch minimum without the glyph shouting for attention.
export function InfoTip({ term, definition, example, className }: Props): ReactNode {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={`Explain ${term}`}
          onClick={(event) => event.stopPropagation()}
          className={cn(
            "relative inline-flex size-5 shrink-0 items-center justify-center rounded-full text-text-tertiary transition-colors before:absolute before:-inset-3 before:content-[''] hover:text-text-secondary focus-visible:shadow-ring-focus",
            className,
          )}
        >
          <Info aria-hidden="true" className="size-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent sideOffset={6} className="max-w-72">
        <div className="flex flex-col gap-1 leading-relaxed">
          <p className="font-medium">{term}</p>
          <p>{definition}</p>
          {example ? <p className="text-2xs text-background/80">{example}</p> : null}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
